import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'motion/react';
import type { FonotekaPack, FonotekaTrack } from '@/api/client';
import { api } from '@/api/client';
import { splitFileNameStemAndSuffix } from '@/components/ProjectFilesPopover/projectFilesPopoverUtils';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { ChoiceDialog } from '@/components/ui/ChoiceDialog/ChoiceDialog';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { MusicTagsCatalogModal } from '@/components/MusicTagsCatalogModal/MusicTagsCatalogModal';
import { patchFonotekaPackInCache, patchFonotekaTrackInCache } from './cache';
import { FONOTEKA_QUERY_KEY } from './queryKeys';
import { FonotekaPackBlock } from './FonotekaPackBlock';
import { FonotekaNameDialog } from './FonotekaNameDialog';
import { FonotekaProjectDialog } from './FonotekaProjectDialog';
import { FonotekaUploadDialog } from './FonotekaUploadDialog';
import { toggleExclusiveId } from './packTriState';
import { Button } from './ui/button';

type RenameTarget =
  | { kind: 'pack'; id: string; name: string }
  | { kind: 'track'; id: string; name: string; suffix: string };

type UploadTarget = { packId: string; packName: string };

export function FonotekaCatalog() {
  const queryClient = useQueryClient();
  const { capabilities } = useAuth();
  const { showError, showSuccess } = useToast();
  const canWrite = capabilities.fonoteka.write;
  const canPickAuthor = capabilities.legal_entities.read;

  const [expandedPackId, setExpandedPackId] = useState<string | null>(null);
  const [tagsCatalogOpen, setTagsCatalogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);
  const [newPackOpen, setNewPackOpen] = useState(false);
  const [nameDialogError, setNameDialogError] = useState<string | null>(null);
  const [deletePackTarget, setDeletePackTarget] = useState<FonotekaPack | null>(null);
  const [deleteTrackTarget, setDeleteTrackTarget] = useState<FonotekaTrack | null>(null);
  const [tagsSavingTrackId, setTagsSavingTrackId] = useState<string | null>(null);
  const [authorSavingTrackId, setAuthorSavingTrackId] = useState<string | null>(null);
  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);
  const uploadTargetRef = useRef<UploadTarget | null>(null);
  uploadTargetRef.current = uploadTarget;

  const listQuery = useQuery({
    queryKey: FONOTEKA_QUERY_KEY,
    queryFn: () => api.fonoteka.listPacks(),
  });

  const packs = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const togglePack = useCallback((packId: string) => {
    setExpandedPackId((prev) => toggleExclusiveId(prev, packId));
  }, []);

  const takenProjectIds = useMemo(
    () => new Set(packs.flatMap((pack) => (pack.project_id ? [pack.project_id] : []))),
    [packs],
  );

  const createPackMutation = useMutation({
    mutationFn: (projectId: string) => api.fonoteka.createPack({ project_id: projectId }),
    onSuccess: (pack) => {
      patchFonotekaPackInCache(queryClient, pack);
      setNameDialogError(null);
      setNewPackOpen(false);
      setExpandedPackId(pack.id);
      setUploadTarget({ packId: pack.id, packName: pack.name });
      showSuccess('Проект добавлен в Фонотеку');
    },
    onError: (e: Error) => {
      setNameDialogError(e.message);
    },
  });

  const renamePackMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.fonoteka.patchPack(id, { name }),
    onSuccess: (pack) => {
      patchFonotekaPackInCache(queryClient, pack);
      setNameDialogError(null);
      setRenameTarget(null);
      showSuccess('Проект переименован');
    },
    onError: (e: Error) => {
      setNameDialogError(e.message);
    },
  });

  const deletePackMutation = useMutation({
    mutationFn: (id: string) => api.fonoteka.deletePack(id),
    onSuccess: (_void, id) => {
      patchFonotekaPackInCache(queryClient, { id, deleted: true });
      setDeletePackTarget(null);
      setExpandedPackId((prev) => (prev === id ? null : prev));
      showSuccess('Проект удалён из Фонотеки');
    },
    onError: (e: Error) => showError(e.message),
  });

  const renameTrackMutation = useMutation({
    mutationFn: ({ id, file_name }: { id: string; file_name: string }) =>
      api.fonoteka.patchTrack(id, { file_name }),
    onSuccess: (track) => {
      patchFonotekaTrackInCache(queryClient, track);
      setRenameTarget(null);
      showSuccess('Трек переименован');
    },
    onError: (e: Error) => showError(e.message),
  });

  const deleteTrackMutation = useMutation({
    mutationFn: (id: string) => api.fonoteka.deleteTrack(id),
    onSuccess: (_void, id) => {
      const track = deleteTrackTarget;
      if (track) {
        patchFonotekaTrackInCache(queryClient, { id, pack_id: track.pack_id, deleted: true });
      }
      setDeleteTrackTarget(null);
      showSuccess('Трек удалён');
    },
    onError: (e: Error) => showError(e.message),
  });

  const putTagsMutation = useMutation({
    mutationFn: ({ trackId, tag_ids }: { trackId: string; tag_ids: string[] }) =>
      api.fonoteka.putTrackTags(trackId, tag_ids),
    onMutate: ({ trackId }) => setTagsSavingTrackId(trackId),
    onSuccess: (track) => {
      patchFonotekaTrackInCache(queryClient, track);
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setTagsSavingTrackId(null),
  });

  const handleUploadFile = useCallback(async (file: File, onProgress: (pct: number) => void) => {
    const target = uploadTargetRef.current;
    if (!target) throw new Error('Проект не выбран');
    const track = await api.fonoteka.uploadTrack(target.packId, file, onProgress);
    patchFonotekaTrackInCache(queryClient, track);
    setExpandedPackId(track.pack_id);
  }, [queryClient]);

  const setAuthorMutation = useMutation({
    mutationFn: ({ trackId, legal_entity_id }: { trackId: string; legal_entity_id: string | null }) =>
      api.fonoteka.patchTrack(trackId, { legal_entity_id }),
    onMutate: ({ trackId }) => setAuthorSavingTrackId(trackId),
    onSuccess: (track) => {
      patchFonotekaTrackInCache(queryClient, track);
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setAuthorSavingTrackId(null),
  });

  const handleToggleTrackTag = useCallback(
    (trackId: string, tagId: string, on: boolean) => {
      if (!canWrite) return;
      const pack = packs.find((p) => p.tracks.some((t) => t.id === trackId));
      const track = pack?.tracks.find((t) => t.id === trackId);
      if (!track) return;
      const next = new Set(track.tags.map((t) => t.id));
      if (on) next.add(tagId);
      else next.delete(tagId);
      putTagsMutation.mutate({ trackId, tag_ids: Array.from(next) });
    },
    [canWrite, packs, putTagsMutation],
  );

  const handleSetTrackAuthor = useCallback(
    (trackId: string, legalEntityId: string | null) => {
      if (!canWrite || !canPickAuthor) return;
      setAuthorMutation.mutate({ trackId, legal_entity_id: legalEntityId });
    },
    [canWrite, canPickAuthor, setAuthorMutation],
  );

  const renameDialogOpen = renameTarget !== null;
  const renameInitialName = renameTarget?.name ?? '';
  const renameDialogTitle =
    renameTarget?.kind === 'track' ? 'Переименовать трек' : 'Переименовать проект';
  const renameDialogLabel =
    renameTarget?.kind === 'track' ? 'Название трека' : 'Название проекта';

  return (
    <div className="fonoteka-catalog-host">
      <motion.div
        className="fonoteka-card"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="fonoteka-card__toolbar">
          <h1 className="fonoteka-card__title">Фонотека</h1>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setTagsCatalogOpen(true)}
            >
              Каталог тегов
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!canWrite}
              onClick={() => setNewPackOpen(true)}
            >
              Новый проект
            </Button>
          </div>
        </div>

        <OverlayScrollArea className="fonoteka-card__body" defer={false}>
          {listQuery.isLoading ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">Загрузка каталога…</div>
          ) : listQuery.isError ? (
            <div className="px-4 py-12 text-center text-sm text-destructive">
              {(listQuery.error as Error).message || 'Не удалось загрузить каталог'}
            </div>
          ) : packs.length === 0 ? (
            <div className="flex flex-col items-center gap-4 px-4 py-12 text-center">
              <p className="text-sm text-muted-foreground">Проектов пока нет</p>
              <Button size="sm" disabled={!canWrite} onClick={() => setNewPackOpen(true)}>
                Новый проект
              </Button>
            </div>
          ) : (
            <div>
              {packs.map((pack) => (
                <FonotekaPackBlock
                  key={pack.id}
                  pack={pack}
                  expanded={expandedPackId === pack.id}
                  onToggle={() => togglePack(pack.id)}
                  canWrite={canWrite}
                  canPickAuthor={canPickAuthor}
                  onRenamePack={() => setRenameTarget({ kind: 'pack', id: pack.id, name: pack.name })}
                  onDeletePack={() => setDeletePackTarget(pack)}
                  onOpenUpload={() => {
                    if (!canWrite) return;
                    setUploadTarget({ packId: pack.id, packName: pack.name });
                  }}
                  onRenameTrack={(track) => {
                    const { stem, suffix } = splitFileNameStemAndSuffix(track.file_name);
                    setRenameTarget({
                      kind: 'track',
                      id: track.id,
                      name: stem || track.file_name,
                      suffix,
                    });
                  }}
                  onDeleteTrack={setDeleteTrackTarget}
                  onOpenTagsCatalog={() => setTagsCatalogOpen(true)}
                  onToggleTrackTag={handleToggleTrackTag}
                  onSetTrackAuthor={handleSetTrackAuthor}
                  tagsSavingTrackId={tagsSavingTrackId}
                  authorSavingTrackId={authorSavingTrackId}
                  uploadBusy={uploadTarget !== null}
                />
              ))}
            </div>
          )}
        </OverlayScrollArea>
      </motion.div>

      <FonotekaUploadDialog
        open={uploadTarget !== null}
        packName={uploadTarget?.packName ?? ''}
        onClose={() => setUploadTarget(null)}
        uploadFile={handleUploadFile}
        onUploaded={(count) => {
          showSuccess(count === 1 ? 'Трек загружен' : `Загружено треков: ${count}`);
        }}
      />

      <MusicTagsCatalogModal open={tagsCatalogOpen} onClose={() => setTagsCatalogOpen(false)} />

      <FonotekaProjectDialog
        open={newPackOpen}
        takenProjectIds={takenProjectIds}
        saving={createPackMutation.isPending}
        error={newPackOpen ? nameDialogError : null}
        onClose={() => {
          setNameDialogError(null);
          setNewPackOpen(false);
        }}
        onPick={(project) => createPackMutation.mutate(project.id)}
      />

      <FonotekaNameDialog
        open={renameDialogOpen}
        title={renameDialogTitle}
        label={renameDialogLabel}
        initialName={renameInitialName}
        confirmLabel="Сохранить"
        saving={renamePackMutation.isPending || renameTrackMutation.isPending}
        error={renameTarget?.kind === 'pack' ? nameDialogError : null}
        onClearError={() => setNameDialogError(null)}
        onClose={() => {
          setNameDialogError(null);
          setRenameTarget(null);
        }}
        onConfirm={async (name) => {
          if (!renameTarget) return;
          if (renameTarget.kind === 'pack') {
            await renamePackMutation.mutateAsync({ id: renameTarget.id, name });
          } else {
            const file_name = renameTarget.suffix ? `${name}${renameTarget.suffix}` : name;
            await renameTrackMutation.mutateAsync({ id: renameTarget.id, file_name });
          }
        }}
      />

      <ChoiceDialog
        open={deletePackTarget !== null}
        onClose={() => setDeletePackTarget(null)}
        title="Удалить проект из Фонотеки?"
        description={
          deletePackTarget ? (
            <>
              Проект «{deletePackTarget.name}» и все его треки ({deletePackTarget.tracks.length}) будут
              удалены из Фонотеки без возможности восстановления. Сам проект останется.
            </>
          ) : null
        }
        actions={[
          {
            label: 'Отмена',
            variant: 'outlined',
            onClick: () => setDeletePackTarget(null),
          },
          {
            label: 'Удалить',
            variant: 'danger',
            disabled: deletePackMutation.isPending,
            onClick: () => {
              if (deletePackTarget) deletePackMutation.mutate(deletePackTarget.id);
            },
          },
        ]}
      />

      <ChoiceDialog
        open={deleteTrackTarget !== null}
        onClose={() => setDeleteTrackTarget(null)}
        title="Удалить трек?"
        description={
          deleteTrackTarget ? (
            <>Трек «{deleteTrackTarget.file_name}» будет удалён без возможности восстановления.</>
          ) : null
        }
        actions={[
          {
            label: 'Отмена',
            variant: 'outlined',
            onClick: () => setDeleteTrackTarget(null),
          },
          {
            label: 'Удалить',
            variant: 'danger',
            disabled: deleteTrackMutation.isPending,
            onClick: () => {
              if (deleteTrackTarget) deleteTrackMutation.mutate(deleteTrackTarget.id);
            },
          },
        ]}
      />
    </div>
  );
}
